import { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';

const COMIC_COLORS = ['#ffde00', '#ff4d2e', '#2563eb', '#22c55e', '#ffffff', '#111111'];

export interface FireConfettiOptions {
  /** Palette; defaults to the comic palette. Pass team colours for a team win. */
  colors?: string[];
  /** `cannons` = two side cannons (win), `pop` = one burst from `origin`. */
  kind?: 'cannons' | 'pop' | 'rain';
  /** 0..1 viewport coordinates for `pop`. */
  origin?: { x: number; y: number };
  /** Particle multiplier (default 1). */
  intensity?: number;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
}

/** Fire comic confetti (square + circle chunks in the comic palette). No-op under reduced motion. */
export function fireConfetti({
  colors = COMIC_COLORS,
  kind = 'cannons',
  origin = { x: 0.5, y: 0.45 },
  intensity = 1,
}: FireConfettiOptions = {}) {
  if (typeof window === 'undefined' || prefersReducedMotion()) return;
  try {
    const base = {
      colors,
      shapes: ['square', 'circle'] as confetti.Shape[],
      scalar: 1.15,
      disableForReducedMotion: true,
      zIndex: 150,
    };
    if (kind === 'pop') {
      void confetti({
        ...base,
        particleCount: Math.round(90 * intensity),
        spread: 80,
        startVelocity: 42,
        origin,
      });
      return;
    }
    if (kind === 'rain') {
      const end = Date.now() + 1600;
      const frame = () => {
        void confetti({
          ...base,
          particleCount: Math.round(4 * intensity),
          startVelocity: 0,
          ticks: 260,
          gravity: 0.8,
          origin: { x: Math.random(), y: -0.05 },
        });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
      return;
    }
    const shoot = (x: number, angle: number) =>
      void confetti({
        ...base,
        particleCount: Math.round(110 * intensity),
        angle,
        spread: 62,
        startVelocity: 62,
        origin: { x, y: 0.75 },
      });
    shoot(0, 60);
    shoot(1, 120);
    setTimeout(() => {
      shoot(0.1, 70);
      shoot(0.9, 110);
    }, 260);
  } catch {
    // Canvas unavailable (tests / old browsers): confetti is decoration only.
  }
}

export interface ConfettiBurstProps extends FireConfettiOptions {
  /** Fires whenever this value changes to a truthy value (e.g. a counter or the winner id). */
  trigger: unknown;
}

/** Declarative confetti: renders nothing, fires `fireConfetti` when `trigger` changes. */
export function ConfettiBurst({ trigger, colors, kind, origin, intensity }: ConfettiBurstProps) {
  const opts = useRef<FireConfettiOptions>({});
  useEffect(() => {
    opts.current = { colors, kind, origin, intensity };
  });
  useEffect(() => {
    if (trigger) fireConfetti(opts.current);
  }, [trigger]);
  return null;
}
